import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useStorage } from '@vueuse/core'
import { useCookie } from '#app'
import { useNuxtApp } from '#app'
import type { Info } from '~/types'

export const useRestaurantStore = defineStore('restaurant', () => {
  const paymentKeysStore = usePaymentKeysStore()
  const categoriesStore = useCategoriesStore()
  const restaurantViewModeStore = useRestaurantViewModeStore()
  const { $axios } = useNuxtApp()
  const slug = ref('')
  const id = ref('')
  const favicon = ref('')
  const experience = ref<any>(process.client ? JSON.parse(localStorage.getItem('experience') || '{}') : {})
  const isLoadingExperience = ref<Boolean>(false)
  const restaurantLogo = ref(process.client ? (localStorage.getItem('logo') || '') : '')
  const info = ref<Info | any>({})
  const payment_methods = ref<string[] | any>([])
  const branchId = ref('')
  const menuIds = ref<string[]>([])
  const activeMenuId = ref('')
  const menus = ref<any[]>([])
  const service_method = ref<string[]>([])
  const selected_service_method = ref<string | null>('dine_in')
  const eligible_branches_count = ref(0)
  const single_eligible_branch_id = ref<string | null>(null)
  const eligible_branches = ref<any[]>([])
  const tableId = ref('');
  const tableName = ref('');

  onMounted(() => {
    if (process.client) {
      // Get data from cookies first, then localStorage as fallback
      getDataFromStorage()
    }
  })

  function normalizeStorageValue(value: string | null | undefined) {
    return value && value !== 'null' && value !== 'undefined' ? value : ''
  }

  function parseStoredMenuIds(value: string | string[] | null | undefined) {
    if (Array.isArray(value)) return value.map(String)
    if (!value) return []

    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      return []
    }
  }

  function setRestaurant(restSlug: string, qr_code: string | '', restMenuIds: string[], restTableId: string | null | undefined, restBranchId: string | null | undefined) {
    if (!restSlug) return
    slug.value = restSlug
    if (!process.client) return
    const normalizedTableId = normalizeStorageValue(restTableId)
    const normalizedBranchId = normalizeStorageValue(restBranchId)

    localStorage.setItem('_slug', restSlug)
    localStorage.setItem('_id', qr_code)
    localStorage.setItem('_menu_ids', JSON.stringify(restMenuIds))
    restaurantViewModeStore.setSlug(restSlug)
    if (normalizedBranchId) {
      localStorage.setItem('_branch_id', normalizedBranchId)
    } else {
      localStorage.removeItem('_branch_id')
    }
    if (normalizedTableId) {
      localStorage.setItem('_table_id', normalizedTableId)
    } else {
      localStorage.removeItem('_table_id')
    }

    const slugCookie = useCookie('_slug', {
      sameSite: 'lax',
      secure: true,
      path: '/',
    })
    slugCookie.value = restSlug

    const idCookie = useCookie('_id', {
      sameSite: 'lax',
      secure: true,
      path: '/',
    })
    idCookie.value = qr_code
    id.value = qr_code

    const menuIdsCookie = useCookie('_menu_ids', {
      sameSite: 'lax',
      secure: true,
      path: '/',
    })
    menuIdsCookie.value = JSON.stringify(restMenuIds)
    menuIds.value = restMenuIds

    const branchIdCookie = useCookie('_branch_id', {
      sameSite: 'lax',
      secure: true,
      path: '/',
    })
    branchIdCookie.value = normalizedBranchId || null
    branchId.value = normalizedBranchId

    const tableIdCookie = useCookie('_table_id', {
      sameSite: 'lax',
      secure: true,
      path: '/',
    })
    if (normalizedTableId) {
      tableIdCookie.value = normalizedTableId
      tableId.value = normalizedTableId
    } else {
      tableIdCookie.value = null
      tableId.value = ''
    }
  }

  async function getDataFromStorage() {
    if (process.client) {
      // Try to get data from cookies first
      tableName.value = useSafeStorage('_table_name') ?? '';
      const slugCookie = useCookie<string>('_slug', {
        sameSite: 'lax',
        secure: true,
        path: '/',
      })
      const idCookie = useCookie<string>('_id', {
        sameSite: 'lax',
        secure: true,
        path: '/',
      })
      const activeMenuIdCookie = useCookie<string>('_active_menu_id', {
        sameSite: 'lax',
        secure: true,
        path: '/',
      })
      const menuIdsCookie = useCookie<string>('_menu_ids', {
        sameSite: 'lax',
        secure: true,
        path: '/',
      })
      const branchIdCookie = useCookie<string>('_branch_id', {
        sameSite: 'lax',
        secure: true,
        path: '/',
      })
      favicon.value = useSafeStorage('favicon') ?? ''
      const tableIdCookie = useCookie<string>('_table_id', {
        sameSite: 'lax',
        secure: true,
        path: '/',
      })
      const selectedServiceMethodCookie = useCookie<string>('_selected_service_method', {
        sameSite: 'lax',
        secure: true,
        path: '/',
      })

      if (slugCookie.value && idCookie.value) {
        slug.value = slugCookie.value
        id.value = idCookie.value
      } else {
        // Fallback to localStorage
        slug.value = useSafeStorage('_slug') ?? ''
        id.value = useSafeStorage('_id') ?? ''

        // If found in localStorage but not in cookies, update cookies
        if (slug.value && id.value && process.client) {
          const newSlugCookie = useCookie('_slug', {
            sameSite: 'lax',
            secure: true,
            path: '/',
          })
          const newIdCookie = useCookie('_id', {
            sameSite: 'lax',
            secure: true,
            path: '/',
          })
          newSlugCookie.value = slug.value
          newIdCookie.value = id.value
        }
      }

      if (!slug.value) {
        const hostSlug = getRestaurantSlugFromHost()
        if (hostSlug) slug.value = hostSlug
      }

      if (activeMenuIdCookie.value) {
        activeMenuId.value = activeMenuIdCookie.value;
      } else {
        activeMenuId.value = useSafeStorage('_active_menu_id') ?? '';
        if (activeMenuId.value && process.client) {
          const newMenuIdCookie = useCookie('_active_menu_id', {
            sameSite: 'lax',
            secure: true,
            path: '/',
          })
          newMenuIdCookie.value = activeMenuId.value;
        }
      }

      const storedMenuIds = parseStoredMenuIds(menuIdsCookie.value || useSafeStorage('_menu_ids'))
      menuIds.value = storedMenuIds;
      if (storedMenuIds.length && !menuIdsCookie.value && process.client) {
        const newMenuIdsCookie = useCookie('_menu_ids', {
          sameSite: 'lax',
          secure: true,
          path: '/',
        })
        newMenuIdsCookie.value = JSON.stringify(storedMenuIds);
      }

      const storedBranchId = normalizeStorageValue(branchIdCookie.value || useSafeStorage('_branch_id'))
      if (storedBranchId) {
        branchId.value = storedBranchId;
      } else {
        branchId.value = '';
        branchIdCookie.value = null;
        localStorage.removeItem('_branch_id');
      }
      if (storedBranchId && !branchIdCookie.value) {
        const newBranchIdCookie = useCookie('_branch_id', {
          sameSite: 'lax',
          secure: true,
          path: '/',
        })
        newBranchIdCookie.value = storedBranchId;
      }

      if (tableIdCookie.value) {
        tableId.value = tableIdCookie.value;
      } else {
        tableId.value = useSafeStorage('_table_id') ?? '';
        if (tableId.value && process.client) {
          const tableIdCookie = useCookie('_table_id', {
            sameSite: 'lax',
            secure: true,
            path: '/',
          })
          tableIdCookie.value = tableId.value;
        }
      }

      if (selectedServiceMethodCookie.value) {
        selected_service_method.value = selectedServiceMethodCookie.value;
      } else {
        selected_service_method.value = useSafeStorage('_selected_service_method') ?? '';
        if (selected_service_method.value && process.client) {
          const selectedServiceMethodCookie = useCookie('_selected_service_method', {
            sameSite: 'lax',
            secure: true,
            path: '/',
          })
          selectedServiceMethodCookie.value = selected_service_method.value;
        }
      }
    }
  }

  async function fetchFavicon() {
    try {
      if (!slug.value) return;
      const response = await $axios.get(`/restaurant/settings/favicon/${slug.value}`);
      if (response.data.success && response.data.data.favicon) {
        favicon.value = response.data.data.favicon;
        localStorage.setItem('favicon', favicon.value);
      }
    } catch (error) {
      console.error('Failed to fetch favicon:', error);
    }
  }

  async function fetchLogo() {
    try {
      const response = await $axios.get(`/restaurant/settings/logo/${slug.value}`);
      if (response.data.success && response.data.data?.logo) {
        restaurantLogo.value = response.data.data?.logo ?? "";
        localStorage.setItem('logo', restaurantLogo.value);
      }
    } catch (error) {
      console.error('Failed to fetch restaurantLogo:', error);
    }
  }

  async function businessInfo() {
    try {
      const response = await $axios.get(`/restaurant/profile/contact-info/${slug.value}`);
      if (response.data.data) {
        info.value = response.data.data
      }
    } catch (error) {
      console.error('Failed to fetch businessInfo:', error);
    }
  }

  async function getCustomerExperience() {
    try {
      if (!slug.value) return;

      isLoadingExperience.value = true;
      const response = await $axios.get(`/public/settings/restaurant/${slug.value}${branchId.value ? `/${branchId.value}` : ''}`);
      if (response.data) {
        experience.value = response.data.data;
        if (process.client) {
          localStorage.setItem('experience', JSON.stringify(experience.value));
        }
      }
      isLoadingExperience.value = false;
    } catch (error) {
      isLoadingExperience.value = false;
      console.error('Failed to fetch experience:', error);
    }
  }

  const setTableName = (name: string) => {
    tableName.value = name;
    useSetSafeStorage('_table_name', name);
  }

  const setActiveMenuId = async (id: string, isOpen?: boolean) => {
    activeMenuId.value = id;
    useSetSafeStorage('_active_menu_id', id);

    if (!isOpen) {
      restaurantViewModeStore.setTemporaryViewOnly();
    } else {
      restaurantViewModeStore.restoreOriginalMode();
    }
    await categoriesStore.fetchCategories(true)
  }

  async function fetchMenus() {
    try {
      const params: any = {
        menu_ids: menuIds.value
      };
      if (branchId.value && branchId.value !== 'null') {
        params.branch_id = branchId.value;
      }
      const response = await $axios.get(`/restaurants/menus-by-ids`, {
        params,
      })
      const fetchedData = response.data.data || response.data;
      const mergedData = Array.isArray(fetchedData)
        ? fetchedData.map((fetchedMenu: any) => {
          const existingMenu = menus.value.find((m: any) => String(m.id) === String(fetchedMenu.id))
          if (existingMenu) {
            return {
              ...existingMenu,
              ...fetchedMenu,
            }
          }
          return fetchedMenu
        })
        : fetchedData;
      menus.value = mergedData;
      return mergedData;
    } catch (error) {
      console.error('Failed to fetch menuIds:', error);
    }
  }

  async function fetchQRcode(code: string) {
    try {
      const data = await $axios.get(`/restaurants/qr-codes/${code}`)
      menus.value = data.data.menus || [];
      service_method.value = data.data.service_method;
      eligible_branches_count.value = data.data.eligible_branches_count || 0;
      single_eligible_branch_id.value = data.data.single_eligible_branch_id || null;
      eligible_branches.value = data.data.eligible_branches || [];
      if (service_method.value.length === 1 || data.data.order_mode === 'view_only') {
        selectServiceMethod(service_method.value[0]);
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch qrcode:', error);
    }
  }
  async function paymentsMethods() {
    try {
      if (!slug.value) return;
      const response = await $axios.get(`/payments/methods/restaurant/${slug.value}`);
      if (response.data.payment_methods.length) {
        payment_methods.value = response.data.payment_methods
      }
      if (payment_methods.value.includes('stripe')) {
        await paymentKeysStore.getRestaurantAccountId(slug.value);
      }
    } catch (error) {
      console.error('Failed to fetch qrcode:', error);
    }
  }
  const selectServiceMethod = (slug: string) => {
    if (menus.value.length === 1) {
      const isOpen = isMenuOpen(menus.value[0]?.id, slug)
      setActiveMenuId(menus.value[0]?.id, isOpen)
    };

    selected_service_method.value = slug;
    useSetSafeStorage('_selected_service_method', slug);
  }
  const parseAvailabilityTime = (time?: string | null) => {
    if (!time) return null
    const [hours, minutes] = String(time).split(':').map(Number)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
    return (hours * 60) + minutes
  }

  const isMenuOpen = (menuId: string | number, method?: string | null) => {
    const menu = menus.value.find((m: any) => String(m.id) === String(menuId));
    if (!menu) {
      return false;
    }

    if (!menu.availability && !menu.use_general_hours) {
      return false;
    }

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayIndex = now.getDay();
    const currentDay = dayNames[currentDayIndex];
    const previousDay = dayNames[(currentDayIndex + 6) % 7];
    const currentTime = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
    const currentMinutes = (now.getHours() * 60) + now.getMinutes();

    const activeBranch = eligible_branches.value.find(
      (b: any) => String(b.id) === String(branchId.value)
    ) || (eligible_branches.value.length === 1 ? eligible_branches.value[0] : null);

    const checkServiceAvailability = (serviceMethod: string) => {
      const useGenHours = menu.use_general_hours && menu.use_general_hours[serviceMethod] === true;
      let serviceSchedule = null;

      if (useGenHours && activeBranch && activeBranch.opening_hours && activeBranch.opening_hours.main_hours) {
        serviceSchedule = activeBranch.opening_hours.main_hours;
      } else {
        serviceSchedule = menu.availability ? menu.availability[serviceMethod] : null;
      }

      if (!serviceSchedule || serviceSchedule.length === 0) {
        return false;
      }

      const currentDaySchedule = serviceSchedule.find((d: any) => d.slug === currentDay);
      const previousDaySchedule = serviceSchedule.find((d: any) => d.slug === previousDay);

      const checkHours = (daySchedule: any, daySlug: string, previousDayOnly = false) => {
        if (!daySchedule || !daySchedule.open) {
          return false;
        }

        const isDayOpen = (daySchedule.hours || []).some((h: any) => {
          const openMinutes = parseAvailabilityTime(h.open?.time);
          const closeMinutes = parseAvailabilityTime(h.close?.time);
          if (openMinutes === null || closeMinutes === null) {
            return true;
          }

          const crossesMidnight = closeMinutes < openMinutes;
          const isOpen = previousDayOnly
            ? crossesMidnight && currentMinutes <= closeMinutes
            : crossesMidnight
              ? currentMinutes >= openMinutes || currentMinutes <= closeMinutes
              : currentMinutes >= openMinutes && currentMinutes <= closeMinutes;

          return isOpen;
        });

        return isDayOpen;
      };

      const isOpenNow = checkHours(currentDaySchedule, currentDay) || checkHours(previousDaySchedule, previousDay, true);

      return isOpenNow;
    };

    if (method) {
      const result = checkServiceAvailability(method);
      return result;
    }

    const availableMethods = Object.keys(menu.availability);
    if (availableMethods.length === 0) {
      return false;
    }
    const result = availableMethods.some(m => checkServiceAvailability(m));
    return result;
  }

  const isBranchOpen = (branch: any) => {
    if (!branch || !branch.opening_hours || !branch.opening_hours.main_hours) {
      return true
    }

    const mainHours = branch.opening_hours.main_hours
    if (!Array.isArray(mainHours) || mainHours.length === 0) {
      return true
    }

    const now = new Date()
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const currentDayIndex = now.getDay()
    const currentDay = dayNames[currentDayIndex]
    const previousDayIndex = (currentDayIndex + 6) % 7
    const previousDay = dayNames[previousDayIndex]

    const currentMinutes = now.getHours() * 60 + now.getMinutes()

    const parseTime = (timeStr: string) => {
      if (!timeStr) return null
      const [h, m] = timeStr.split(':').map(Number)
      if (isNaN(h) || isNaN(m)) return null
      return h * 60 + m
    }

    const currentDaySchedule = mainHours.find((d: any) => d.slug === currentDay)
    const previousDaySchedule = mainHours.find((d: any) => d.slug === previousDay)

    const checkSchedule = (schedule: any, previousDayOnly = false) => {
      if (!schedule || !schedule.open) return false
      const hours = schedule.hours || []
      if (hours.length === 0) return true

      return hours.some((h: any) => {
        const openTime = h.open?.time
        const closeTime = h.close?.time
        if (!openTime || !closeTime) return true

        const openMin = parseTime(openTime)
        const closeMin = parseTime(closeTime)
        if (openMin === null || closeMin === null) return true

        const crossesMidnight = closeMin < openMin
        if (previousDayOnly) {
          return crossesMidnight && currentMinutes <= closeMin
        } else {
          if (crossesMidnight) {
            return currentMinutes >= openMin || currentMinutes <= closeMin
          } else {
            return currentMinutes >= openMin && currentMinutes <= closeMin
          }
        }
      })
    }

    return checkSchedule(currentDaySchedule, false) || checkSchedule(previousDaySchedule, true)
  }

  const isCurrentBranchOpen = computed(() => {
    if (!branchId.value) return true
    const branch = eligible_branches.value.find((b: any) => String(b.id) === String(branchId.value))
    if (!branch) return true
    return isBranchOpen(branch)
  })

  const getMenuTodayHoursString = (menuId: string | number, serviceMethod: string) => {
    const menu = menus.value.find((m: any) => String(m.id) === String(menuId));
    if (!menu) return 'Closed';

    const useGenHours = menu.use_general_hours && menu.use_general_hours[serviceMethod] === true;
    let serviceSchedule = null;

    const activeBranch = eligible_branches.value.find(
      (b: any) => String(b.id) === String(branchId.value)
    ) || (eligible_branches.value.length === 1 ? eligible_branches.value[0] : null);

    if (useGenHours && activeBranch && activeBranch.opening_hours && activeBranch.opening_hours.main_hours) {
      serviceSchedule = activeBranch.opening_hours.main_hours;
    } else {
      serviceSchedule = menu.availability ? menu.availability[serviceMethod] : null;
    }

    if (!serviceSchedule || serviceSchedule.length === 0) {
      return 'Closed';
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[new Date().getDay()];
    const currentDaySchedule = serviceSchedule.find((d: any) => d.slug === currentDay);

    if (!currentDaySchedule || !currentDaySchedule.open) {
      return 'Closed today';
    }

    const hours = currentDaySchedule.hours || [];
    if (hours.length === 0) {
      return 'Open';
    }

    return hours.map((h: any) => {
      const openTime = h.open?.time || '00:00';
      const closeTime = h.close?.time || '00:00';
      return `${openTime} – ${closeTime}`;
    }).join(', ');
  }

  const syncViewModeWithAvailability = () => {
    if (!isCurrentBranchOpen.value) {
      restaurantViewModeStore.setTemporaryViewOnly();
      return
    }

    if (activeMenuId.value) {
      const isOpen = isMenuOpen(activeMenuId.value, selected_service_method.value)
      if (!isOpen) {
        restaurantViewModeStore.setTemporaryViewOnly();
      } else {
        restaurantViewModeStore.restoreOriginalMode();
      }
      return
    }
  }

  watch([isCurrentBranchOpen, activeMenuId, selected_service_method], () => {
    syncViewModeWithAvailability()
  }, { immediate: true })

  const dismissedBranchClosedPopups = ref<string[]>([])
  const dismissedMenuClosedPopups = ref<string[]>([])

  const dismissBranchClosedPopup = (branchId: string | number) => {
    if (!dismissedBranchClosedPopups.value.includes(String(branchId))) {
      dismissedBranchClosedPopups.value.push(String(branchId))
    }
  }

  const dismissMenuClosedPopup = (menuId: string | number) => {
    if (!dismissedMenuClosedPopups.value.includes(String(menuId))) {
      dismissedMenuClosedPopups.value.push(String(menuId))
    }
  }

  const isBranchClosedPopupDismissed = (branchId: string | number) => {
    return dismissedBranchClosedPopups.value.includes(String(branchId))
  }

  const isMenuClosedPopupDismissed = (menuId: string | number) => {
    return dismissedMenuClosedPopups.value.includes(String(menuId))
  }

  return {
    dismissedBranchClosedPopups,
    dismissedMenuClosedPopups,
    dismissBranchClosedPopup,
    dismissMenuClosedPopup,
    isBranchClosedPopupDismissed,
    isMenuClosedPopupDismissed,
    slug,
    id,
    activeMenuId,
    menuIds,
    branchId,
    menus,
    selected_service_method,
    service_method,
    eligible_branches_count,
    single_eligible_branch_id,
    eligible_branches,
    tableId,
    tableName,
    favicon,
    experience,
    isLoadingExperience,
    restaurantLogo,
    info,
    payment_methods,

    setRestaurant,
    getDataFromStorage,
    businessInfo,
    fetchFavicon,
    getCustomerExperience,
    fetchLogo,
    fetchQRcode,
    paymentsMethods,
    setActiveMenuId,
    fetchMenus,
    setTableName,
    selectServiceMethod,
    isMenuOpen,
    isBranchOpen,
    isCurrentBranchOpen,
    getMenuTodayHoursString,
    syncViewModeWithAvailability
  }
})
